import { DollarSign, MapPin, Phone, Mail, Clock, Shield, Users, CheckCircle, Star, Award, CreditCard, Zap, Building, FileText, Calculator, TrendingUp, BookOpen, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="https://quickloanpro.com/storage/2023/11/Payday-Loans-Louisiana-Logo.png" 
                alt="QuickLoanPro Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">QuickLoanPro</h1>
                <p className="text-gray-600">Your Trusted Financial Partner</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-gray-700">+1 (504) 438-1753</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-gray-200 transition-colors">Home</Link>
            <Link to="/about" className="hover:text-gray-200 transition-colors">About Us</Link>
            <Link to="/contact" className="hover:text-gray-200 transition-colors">Contact</Link>
            <Link to="/privacy" className="hover:text-gray-200 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="font-semibold">Terms of Service</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-gray-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Terms of Service
          </h2>
          <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
            Please read these terms carefully before using QuickLoanPro services. By using our services, you agree to these terms and conditions.
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <CardContent className="prose max-w-none">
                <p className="text-sm text-gray-600 mb-8">
                  <strong>Effective Date:</strong> January 1, 2025<br />
                  <strong>Last Updated:</strong> January 1, 2025
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">1. Acceptance of Terms</h3>
                <p className="mb-6 text-gray-700">
                  By accessing or using QuickLoanPro services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using our services.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">2. Loan Services</h3>
                <p className="mb-4 text-gray-700">
                  QuickLoanPro provides short-term lending services including:
                </p>
                <ul className="list-disc list-inside mb-6 text-gray-700 space-y-2">
                  <li>Payday loans up to $1,000</li>
                  <li>Personal loans up to $5,000</li>
                  <li>Installment loans with fixed payment schedules</li>
                  <li>Cash advances for emergency situations</li>
                  <li>Title loans secured by vehicle collateral</li>
                </ul>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">3. Eligibility Requirements</h3>
                <p className="mb-4 text-gray-700">
                  To be eligible for our services, you must:
                </p>
                <ul className="list-disc list-inside mb-6 text-gray-700 space-y-2">
                  <li>Be at least 18 years of age</li>
                  <li>Be a U.S. citizen or permanent resident</li>
                  <li>Have a valid government-issued ID</li>
                  <li>Have a steady source of income</li>
                  <li>Have an active checking account</li>
                  <li>Meet our creditworthiness criteria</li>
                </ul>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">4. Loan Terms and Conditions</h3>
                <p className="mb-4 text-gray-700">
                  All loans are subject to the following:
                </p>
                <ul className="list-disc list-inside mb-6 text-gray-700 space-y-2">
                  <li>Interest rates and fees as disclosed in your loan agreement</li>
                  <li>Repayment terms specified in your loan contract</li>
                  <li>Late payment fees and penalties as applicable</li>
                  <li>State and federal lending regulations</li>
                  <li>Our underwriting and approval process</li>
                </ul>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">5. Repayment Obligations</h3>
                <p className="mb-6 text-gray-700">
                  You agree to repay your loan according to the terms specified in your loan agreement. This includes making payments on time and in full. Failure to repay may result in additional fees, collection activities, and negative credit reporting.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">6. Privacy and Data Protection</h3>
                <p className="mb-6 text-gray-700">
                  Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your personal information. By using our services, you consent to our data practices as described in our Privacy Policy.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">7. Prohibited Uses</h3>
                <p className="mb-4 text-gray-700">
                  You may not use our services for:
                </p>
                <ul className="list-disc list-inside mb-6 text-gray-700 space-y-2">
                  <li>Any unlawful purpose or activity</li>
                  <li>Fraudulent or deceptive practices</li>
                  <li>Providing false or misleading information</li>
                  <li>Attempting to circumvent our security measures</li>
                  <li>Interfering with our website or services</li>
                </ul>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">8. Limitation of Liability</h3>
                <p className="mb-6 text-gray-700">
                  QuickLoanPro shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services. Our total liability shall not exceed the amount of fees paid by you for the specific service giving rise to the claim.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">9. Modifications to Terms</h3>
                <p className="mb-6 text-gray-700">
                  We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services after any changes constitutes acceptance of the new terms.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">10. Governing Law</h3>
                <p className="mb-6 text-gray-700">
                  These Terms of Service shall be governed by and construed in accordance with the laws of the State of Louisiana, without regard to its conflict of law provisions.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-gray-900">11. Contact Information</h3>
                <p className="mb-4 text-gray-700">
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    <strong>QuickLoanPro</strong><br />
                    4901 Bloomfield St<br />
                    New Orleans, LA 70121<br />
                    Phone: +1 (504) 438-1753<br />
                    Email: info@quickloanpro.com
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">QuickLoanPro</h3>
              <p className="text-gray-400 mb-4">
                Your trusted partner for loans and financial assistance across Louisiana.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (504) 438-1753</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@quickloanpro.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>4901 Bloomfield St, New Orleans, LA 70121</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Loan Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/payday-loans" className="hover:text-white">Payday Loans</Link></li>
                <li><Link to="/personal-loans" className="hover:text-white">Personal Loans</Link></li>
                <li><Link to="/installment-loans" className="hover:text-white">Installment Loans</Link></li>
                <li><Link to="/cash-advances" className="hover:text-white">Cash Advances</Link></li>
                <li><Link to="/title-loans" className="hover:text-white">Title Loans</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/loan-calculator" className="hover:text-white">Loan Calculator</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/responsible-lending" className="hover:text-white">Responsible Lending</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 QuickLoanPro. All rights reserved.</p>
            <p className="mt-2">Licensed financial service provider committed to responsible lending practices.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;